import React from 'react'
import { useState, useEffect } from 'react'
import { Modal, Tabs, Button, Empty, Image, Spin } from 'antd'
import ReactMarkdown from 'react-markdown'
import { Transformer } from 'markmap-lib'
import { loadCSS, loadJS, Markmap } from 'markmap-view'
import styled from '@emotion/styled'
import { maintenanceService } from '../maintenanceService'
import { getBaseImgLookUrl } from '@/utils/constants'

export const ViewReport = (props: any) => {
    const { orderId, visible, maintenanceCode, setViewReportInfo } = props

    useEffect(() => {
        if (visible && orderId) {
            getInfo()
        }
    }, [visible, orderId])

    const [markdownInfo, setMarkdownInfo] = useState<string | null>(null)
    const [activeKey, setactiveKey] = useState<string>('item-2')
    const [orderInfo, setOrderInfo] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [orderInfoLoading, setOrderInfoLoading] = useState<boolean>(false)

    const getInfo = (): void => {
        setLoading(true)
        setOrderInfoLoading(true)

        maintenanceService.orderReportOne(orderId).then((res: any) => {
            if (res) {
                setLoading(false)
                const { retData } = res
                if (retData) {
                    const { report }: any = retData
                    setMarkdownInfo(report)
                    setTimeout(() => {
                        setMarkdownView(report)
                    }, 100)
                }
            }
        }).catch(() => {
            setLoading(false)
        })

        maintenanceService.one(maintenanceCode).then((res: any) => {
            setOrderInfoLoading(false)
            const { retData } = res
            if (retData) {
                setOrderInfo(retData)
            }
        }).catch(() => {
            setOrderInfoLoading(false)
        })
    }

    const setMarkdownView = (report?: string): void => {
        const transformer = new Transformer()
        const { root, features } = transformer.transform(markdownInfo || report || '')
        const { styles, scripts } = transformer.getUsedAssets(features)

        if (styles) loadCSS(styles)

        const svgEl = document.querySelector('#markMap')
        if (svgEl) {
            Markmap.create(svgEl as SVGElement, undefined, root)
        }
    }

    const handleCancel = (): void => {
        setViewReportInfo({
            visible: false,
            orderId: '',
            maintenanceCode: ''
        })
    }

    const onChange = (activeKey: string): void => {
        setactiveKey(activeKey)
        if (activeKey === 'item-2') {
            setTimeout(() => {
                setMarkdownView()
            }, 200)
        }
    }

    // 时间格式化方法
    const formatTime = (timestamp: string | number[] | any): string => {
        if (!timestamp) return '---'

        let date: Date;

        // 处理数组格式的时间 [年, 月, 日, 时, 分, 秒]
        if (Array.isArray(timestamp) && timestamp.length >= 3) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = timestamp;
            date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
        }
        // 处理字符串格式的时间
        else if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        }
        // 处理其他格式
        else {
            date = new Date(timestamp);
        }

        // 确保日期有效
        if (isNaN(date.getTime())) {
            return '---';
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        const second = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }

    // 根据状态获取职位信息
    const getPositionByStatus = (status: string): string => {
        const positionMap: Record<string, string> = {
            '已开单': '由此创建维修工单',
            '待修': '由此转接维修工单',
            '在修': '由此派工给维修技师维修',
            '审核中': '由此提交审核完工申请',
            '审核通过': '由此通过完工申请',
            '已完工': '由此将此维修工单完工',
            '已终检': '由此完成最终检验',
            '已结算': '由此将此维修工单结算完成'
        };

        return positionMap[status] || '未知';
    }

    // 获取进度节点列表
    const getProgressNodes = () => {
        if (!orderInfo) return [];

        // 定义审批流程配置
        const processConfig: Record<string, {
            timeField: any,
            userField: any,
            opinionField?: any,
            finalMsg?: any,
            signatureField?: any
        }> = {
            '已开单': {
                timeField: 'createTime',
                userField: 'createUser',
            },
            '待修': {
                timeField: 'tranTime',
                userField: 'tranUser',
            },
            '在修': {
                timeField: 'dispatchTime',
                userField: 'dispatcher',
            },
            '审核中': {
                timeField: 'examineTime',
                userField: 'examineUser',
            },
            '审核通过': {
                timeField: 'approvedTime',
                userField: 'approvedUser',
            },
            '已完工': {
                timeField: 'completeTime',
                userField: 'completeUser',
            },
            '已终检': {
                timeField: 'finalTime',
                userField: 'finalUser',
                opinionField: 'finalOpinion',
                finalMsg: 'finalMsg',
                signatureField: 'finalSignature'
            },
            '已结算': {
                timeField: 'settleTime',
                userField: 'settleUser',
            }
        };

        const result: any[] = [];
        let hasActiveStatus = false;

        // 遍历配置中的状态
        for (const [status, config] of Object.entries(processConfig)) {
            const timeValue = orderInfo[config.timeField];

            // 检查是否有时间值
            if (timeValue) {
                const approver = orderInfo[config.userField] || '未知';
                const formattedTime = formatTime(timeValue);
                const isActive = !hasActiveStatus;

                if (isActive) {
                    hasActiveStatus = true;
                }

                const itemData: any = {
                    status,
                    approver,
                    position: getPositionByStatus(status),
                    time: formattedTime,
                    active: isActive
                };

                // 对于已终检状态，添加额外信息
                if (status === '已终检') {
                    itemData.finalOpinion = config.opinionField ? orderInfo[config.opinionField] || '' : '';
                    itemData.inspectionImages = config.finalMsg && orderInfo[config.finalMsg]
                        ? (orderInfo[config.finalMsg] as string).split(",").map((item: string) => item)
                        : [];
                    itemData.inspectionSignature = config.signatureField ? orderInfo[config.signatureField] : '';
                }

                result.push(itemData);
            }
        }

        // 如果没有任何状态有值，添加一个默认状态
        if (result.length === 0) {
            result.push({
                status: '待开单',
                approver: '系统',
                position: '自动',
                time: '---',
                active: true
            });
        }

        return result;
    };

    const progressList = getProgressNodes();

    // 渲染加载状态
    const renderLoading = (text: string = '维修报告生成中，请稍后查看') => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px'
        }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#999' }}>{text}</div>
        </div>
    )

    return (
        <Modal
            open={visible}
            title="报告查看"
            footer={[
                <Button onClick={handleCancel} key="back">
                    取消
                </Button>,
            ]}
            onCancel={handleCancel}
            width={'80vw'}
            destroyOnClose={true}
        >
            <Tabs onChange={onChange} activeKey={activeKey} destroyInactiveTabPane={true}>
                <Tabs.TabPane tab="思维导图" key="item-2">
                    {activeKey === 'item-2' && (
                        loading ? renderLoading() :
                            markdownInfo ? <svg id="markMap" style={{ width: '80vw', height: '80vh' }}></svg> :
                                renderLoading()
                    )}
                </Tabs.TabPane>
                <Tabs.TabPane tab="流程分析" key="item-3">
                    {activeKey === 'item-3' && (
                        orderInfoLoading ? renderLoading('加载中...') :
                            orderInfo ? (
                                <div style={{
                                    marginTop: 24,
                                    padding: '0 24px',
                                    maxHeight: '80vh',
                                    overflow: 'auto'
                                }}>
                                    <div style={{ position: 'relative', paddingLeft: '60px' }}>
                                        {progressList.map((item, index) => (
                                            <div key={item.status} style={{ position: 'relative', marginBottom: '24px' }}>
                                                {/* 节点 */}
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '-40px',
                                                    top: '0',
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#00BE87',
                                                    zIndex: 2
                                                }}></div>

                                                {/* 连接线（最后一个节点没有） */}
                                                {index !== progressList.length - 1 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '-29px',
                                                        top: '24px',
                                                        width: '3px',
                                                        height: 'calc(100% + 20px)',
                                                        backgroundColor: '#00be8724',
                                                        zIndex: 1
                                                    }}></div>
                                                )}

                                                {/* 内容 */}
                                                <div style={{
                                                    padding: '10px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e8e8e8',
                                                    backgroundColor: '#f6ffed',
                                                    marginBottom: '5px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '20px',
                                                        color: '#000',
                                                        fontWeight: 'bold',
                                                        marginBottom: '8px'
                                                    }}>
                                                        {item.status}
                                                    </div>

                                                    {/* 审批人信息 */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginBottom: '8px'
                                                    }}>
                                                        {/* 首字头像 */}
                                                        <div style={{
                                                            width: '35px',
                                                            height: '35px',
                                                            backgroundColor: '#00BE87',
                                                            color: 'white',
                                                            fontSize: '20px',
                                                            fontWeight: 'bold',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            marginRight: '12px'
                                                        }}>
                                                            {item.approver.slice(0, 1)}
                                                        </div>

                                                        {/* 审批人姓名+职务 */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '17px',
                                                                fontWeight: '600',
                                                                color: '#303133'
                                                            }}>
                                                                {item.approver}
                                                            </span>
                                                            <span style={{
                                                                fontSize: '15px',
                                                                color: '#606266'
                                                            }}>
                                                                · {item.position}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        fontSize: '14px',
                                                        color: '#969AA4',
                                                        marginTop: '3px'
                                                    }}>
                                                        {item.time}
                                                    </div>

                                                    {/* 终检信息 - 仅在已终检状态显示 */}
                                                    {item.status === '已终检' && (
                                                        <div style={{
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            marginTop: '5px',
                                                            borderTop: '1px dashed #EBEBEB',
                                                            paddingTop: '5px'
                                                        }}>
                                                            {/* 终检意见 */}
                                                            {item.finalOpinion && (
                                                                <div style={{
                                                                    lineHeight: '1.6',
                                                                    marginBottom: '8px'
                                                                }}>
                                                                    <span style={{
                                                                        fontSize: '15px',
                                                                        color: '#303133',
                                                                        fontWeight: '500',
                                                                        marginRight: '10px'
                                                                    }}>
                                                                        终检意见：
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '15px',
                                                                        color: '#606266'
                                                                    }}>
                                                                        {item.finalOpinion}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* 终检图片 */}
                                                            {item.inspectionImages && item.inspectionImages.length > 0 && (
                                                                <div style={{ marginBottom: '8px' }}>
                                                                    <span style={{
                                                                        fontSize: '14px',
                                                                        color: '#303133',
                                                                        fontWeight: '500',
                                                                        marginRight: '10px'
                                                                    }}>
                                                                        终检图片：
                                                                    </span>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: '8px',
                                                                        marginTop: '8px'
                                                                    }}>
                                                                        {item.inspectionImages.map((img: string, imgIndex: number) => (
                                                                            <div
                                                                                key={imgIndex}
                                                                                style={{
                                                                                    width: '100px',
                                                                                    height: '100px',
                                                                                    borderRadius: '8px',
                                                                                    overflow: 'hidden',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            >

                                                                                <Image src={getBaseImgLookUrl(img)} alt={`终检图片${imgIndex + 1}`} />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* 终检签名 */}
                                                            {item.inspectionSignature && (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '10px',
                                                                    marginTop: '16px'
                                                                }}>
                                                                    <span style={{
                                                                        fontSize: '14px',
                                                                        color: '#303133',
                                                                        fontWeight: '500',
                                                                        flexShrink: 0
                                                                    }}>
                                                                        终检签名：
                                                                    </span>
                                                                    <img
                                                                        src={getBaseImgLookUrl(item.inspectionSignature)}
                                                                        alt="终检签名"
                                                                        style={{
                                                                            height: '60px',
                                                                            width: '120px',
                                                                            objectFit: 'contain'
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : <Empty />
                    )}
                </Tabs.TabPane>
                <Tabs.TabPane tab="报告详情" key="item-1">
                    <ReactMarkdownBox>
                        {loading ? renderLoading() :
                            markdownInfo ? <ReactMarkdown>{markdownInfo}</ReactMarkdown> :
                                renderLoading()}
                    </ReactMarkdownBox>
                </Tabs.TabPane>
            </Tabs>
        </Modal>
    )
}

const ReactMarkdownBox = styled.div`
    p {
        color: #4165e3;
        font-size: 14px;
        margin-top: 5px;
    }

    ul > li > strong {
        color: #1d1e21;
    }

    ul > li {
        color: #9b9ea5;
        font-size: 12px;
    }
`